import {
  type CartReason,
  daysBetween,
  type IsoDate,
  itemKey,
  type LocationType,
  type NewCartEntry,
  type NewItem,
  type ShopLocation,
  shouldOfferRebuy,
  type Verdict,
} from "@larder/shared";
import { useState } from "react";
import { useHasCamera } from "./camera/useHasCamera";
import { AddFab } from "./components/AddFab";
import { BottomNav, type Tab } from "./components/BottomNav";
import { InstallNudge } from "./components/InstallNudge";
import { Loading } from "./components/Loading";
import { NotifyPrimer } from "./components/NotifyPrimer";
import { RebuyPrompt } from "./components/RebuyPrompt";
import { SignIn } from "./components/SignIn";
import { TopBar } from "./components/TopBar";
import { Diagnostics } from "./Diagnostics";
import { addToCart, readProductMemory, recordVerdict, removeFromCart } from "./data/cart";
import { addItems, countActiveItems, editItem, resolveItem } from "./data/items";
import {
  createLocation,
  deleteLocation,
  renameLocation,
  setShopLocation,
} from "./data/locations";
import { useSession } from "./data/session";
import { createStorage, deleteStorage } from "./data/storages";
import { useCart } from "./data/useCart";
import { useEnrichment } from "./data/useEnrichment";
import { useLarder } from "./data/useLarder";
import { useLocations } from "./data/useLocations";
import { useStorages } from "./data/useStorages";
import { useInstall } from "./install/useInstall";
import { usePush } from "./push/usePush";
import { AddItem } from "./screens/AddItem";
import { Calendar } from "./screens/Calendar";
import { CartDetail } from "./screens/CartDetail";
import { CreateLocation } from "./screens/CreateLocation";
import { InstallApp } from "./screens/InstallApp";
import { ItemDetail } from "./screens/ItemDetail";
import { Landing } from "./screens/Landing";
import { Larder } from "./screens/Larder";
import { Settings } from "./screens/Settings";
import { cue, useCues } from "./ui/feedback";

/**
 * A view state rather than a router. None of these screens is worth a URL yet,
 * and a back button that always means "the larder" is easier to reason about
 * than history entries. A router lands when deep links do — the check-in push
 * is what will need one.
 *
 * The four TABS get the shell (top bar + bottom nav). The three FLOWS — scan,
 * add, name a place — take over the whole screen, because a task you are in the
 * middle of should not offer you four ways to abandon it.
 */
type View = Tab | "add" | "new-place" | "item" | "cart-item";

export default function App() {
  const auth = useSession();
  const { session } = auth;
  const uid = session.state === "signed-in" ? session.uid : null;
  const locations = useLocations(uid);
  const location = locations.current;

  const larder = useLarder(location?.id ?? null, location?.timezone ?? "Asia/Kolkata");
  const storages = useStorages(location?.id ?? null);
  const cart = useCart(location?.id ?? null);
  const install = useInstall(uid);
  const push = usePush(uid);
  // Mounts the sprite and publishes the cue channel. Called here and nowhere
  // else — every other caller reaches it through the module-level `cue()` or
  // through a `data-cue` attribute. See ui/feedback.ts.
  const cues = useCues();

  // Fills in category, pack size, macros and a glyph for anything that lacks
  // them. Runs itself off the item list — nothing else has to remember to.
  useEnrichment(location?.id ?? null, larder.items, location?.timezone ?? "Asia/Kolkata");

  const [view, setView] = useState<View>("larder");
  const [signingIn, setSigningIn] = useState(false);
  const canScan = useHasCamera();
  const [draft, setDraft] = useState<NewItem | null>(null);
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The thing that just ran out, if anything did.
   *
   * Transient on purpose — miss it and it is gone. A persisted queue of "we
   * still owe you a question" would be a second inbox to clear, which is the
   * opposite of one minute a day. See docs/plans/active/0002-cart-and-rebuy.md.
   */
  const [ranOut, setRanOut] = useState<{ id: string; name: string } | null>(null);
  /** The cart entry being bought, cleared once the item actually lands. */
  const [fromCartId, setFromCartId] = useState<string | null>(null);
  /** The cart entry being looked at. Separate from the one being bought. */
  const [openCartId, setOpenCartId] = useState<string | null>(null);
  /** A city resolved this session, before the household document has it. */
  const [shopWhere, setShopWhere] = useState<ShopLocation | null>(null);

  const home = () => {
    setDraft(null);
    setFromCartId(null);
    setOpenCartId(null);
    setError(null);
    setView("larder");
  };

  async function run(work: () => Promise<unknown>, then?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await work();
      then?.();
    } catch (e) {
      // The one place a write is known to have failed, so the one place the
      // failure cue belongs. Every success cue fires from inside `work`, after
      // the await — nothing chimes on the way in.
      cue("error");
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const handleCreatePlace = (name: string, type: LocationType) =>
    run(async () => {
      if (!uid) throw new Error("not signed in");
      const made = await createLocation(
        uid,
        { name, type },
        locations.locations.map((l) => l.name),
      );
      locations.select(made.id);
    }, home);

  /**
   * The one place that decides a moment is worth asking about reminders.
   *
   * Both triggers come through here — adding something with a date, and opening
   * something with a date — because they are the same argument: a date now
   * exists, and a date nobody is watching is just a number. Undated items are
   * silently skipped; there is nothing to remind anyone about, and asking there
   * would be the generic pitch wearing a contextual trigger.
   *
   * `push.consider` applies the actual rules (supported? already on? already
   * asked this session? still inside the backoff?), so this can be called
   * freely from anywhere without each caller re-deriving them.
   */
  const considerReminders = (name: string, expiry: IsoDate | null) => {
    if (!expiry) return;
    push.consider({ name, daysLeft: daysBetween(larder.today, expiry) });
  };

  /**
   * Opening an item, from the list or from a related one inside the detail.
   *
   * Both routes go through here so the reminder ask fires wherever an item with
   * a date is actually looked at — an item opened from a "similar things" row
   * is being looked at just as much as one opened from the list.
   */
  const goToItem = (id: string) => {
    setOpenItemId(id);
    setView("item");
    const opened = larder.items.find((i) => i.id === id);
    if (opened) considerReminders(opened.name, opened.derived.effectiveExpiry);
  };

  const handleAdd = (items: NewItem[]) =>
    run(async () => {
      if (!uid || !location) throw new Error("no household");
      const item = items[0];
      if (!item) return;
      // Three packs of one thing are three items and one arrival, so they
      // land in one batch — see shared/model/batch.ts.
      await addItems(location.id, uid, items);
      // Buying is what takes something off the list. The new item's `added`
      // event is the durable record that it happened, so the cart entry can
      // simply go — it never needs a history of its own.
      if (fromCartId) await removeFromCart(location.id, fromCartId);
      // Not `remove`, even though a cart line just went: one thing happened
      // here, and it was an arrival. The line leaving is bookkeeping.
      cue("add");
      considerReminders(item.name, item.expiryDate ?? null);
    }, home);

  /**
   * Creates a storage place from inside the add-item flow.
   *
   * Defaults to `pantry` because that is what an unqualified "cupboard" is, and
   * asking a second question here would defeat the point of not sending the
   * user to settings. The kind is editable there afterwards.
   */
  const handleAddPlace = async (name: string): Promise<string | null> => {
    if (!location) return null;
    try {
      return await createStorage(
        location.id,
        { name, kind: "pantry" },
        storages.places.length,
      );
    } catch (e) {
      setError((e as Error).message);
      return null;
    }
  };

  /**
   * Records the answer, then asks the only follow-up worth asking.
   *
   * The rebuy question fires ONLY when the house is now out of the thing — the
   * gate lives in `shouldOfferRebuy`, which reads the same `isSameThing`
   * matcher that groups the list, so the prompt can never claim you have run
   * out of something the screen is showing you two of.
   *
   * Stock is checked against the pre-write list, which is correct: the item
   * just resolved is excluded by id, and every OTHER item's status is
   * unaffected by this write.
   */
  const handleAnswer = (itemId: string, type: "ate" | "tossed" | "still_here") =>
    run(async () => {
      if (!uid || !location) throw new Error("no household");
      const eaten = type === "ate" ? larder.items.find((i) => i.id === itemId) : null;
      await resolveItem(location.id, uid, itemId, type);
      if (!eaten) return;

      const memory = await readProductMemory(location.id, itemKey(eaten.name));
      const offer = shouldOfferRebuy({
        name: eaten.name,
        itemId,
        // Projected onto the DERIVED status. `item.status` is a cache and must
        // never be read as truth (ADR 0010).
        items: larder.items.map((i) => ({
          id: i.id,
          name: i.name,
          status: i.derived.status,
        })),
        cart: cart.entries,
        memory,
      });
      if (offer) setRanOut({ id: itemId, name: eaten.name });
    });

  const handleVerdict = (verdict: Verdict) => {
    if (!uid || !location || !ranOut) return;
    // Deliberately not awaited and not gated on `busy`: the verdict is recorded
    // for its own sake, and making someone wait for a write before they can
    // answer the next question would be the tax this prompt exists to avoid.
    void recordVerdict(location.id, uid, {
      itemId: ranOut.id,
      name: ranOut.name,
      verdict,
    });
  };

  const handleAddToCart = (input: NewCartEntry) =>
    run(async () => {
      if (!uid || !location) throw new Error("no household");
      await addToCart(location.id, uid, input, cart.entries);
      // Every route onto the shopping list lands here — the rebuy prompt, a
      // search that found nothing, "add to shopping list" on the item screen —
      // so one call covers all three and none of them has to know about cues.
      cue("add");
    });

  /** Ticking something off opens the add flow prefilled, rather than */
  /** inventing an item with no date — the date is the whole product. */
  const handleBuy = (cartId: string) => {
    const entry = cart.entries.find((e) => e.id === cartId);
    if (!entry) return;
    setFromCartId(cartId);
    setDraft({
      name: entry.name,
      brand: entry.brand,
      sizeText: entry.sizeText,
      qty: entry.qty,
    });
    setView("add");
  };

  /**
   * Opening a line on the shopping list.
   *
   * Deliberately NOT `handleBuy` — that one says the thing is now in the house
   * and hands over to the add form. This one only says you are looking at it.
   */
  const openCart = (cartId: string) => {
    setOpenCartId(cartId);
    setError(null);
    setView("cart-item");
  };

  // ── States before there is anything to show ──────────────────────────
  if (session.state === "error") {
    return (
      <Shell>
        <Blocked title="Can't sign in" detail={session.message} />
      </Shell>
    );
  }
  if (session.state === "loading") {
    return (
      <Shell>
        <Loading label="Getting your larder" />
      </Shell>
    );
  }
  if (session.state === "signed-out") {
    return (
      <Shell wide>
        <Landing onStart={() => setSigningIn(true)} busy={auth.busy} />
        {signingIn && <SignIn api={auth} onClose={() => setSigningIn(false)} />}
        <Diagnostics session={session} />
      </Shell>
    );
  }
  if (locations.status === "error") {
    return (
      <Shell>
        <Blocked title="Can't reach your data" detail={locations.error ?? "unknown"} />
      </Shell>
    );
  }
  if (locations.status === "loading" || !location) {
    return (
      <Shell>
        <Loading
          label="Setting up your kitchen"
          hint="Making a place called Home and a few spots to put things in. Just this once."
        />
      </Shell>
    );
  }

  /**
   * Installing, before the larder.
   *
   * Deliberately ABOVE the tabs and after the household exists: it is the last
   * onboarding step, not a screen you navigate to. It returns every session
   * until the app is actually installed, which is what reaches the users who
   * signed up before this existed — see
   * docs/decisions/0019-install-is-onboarding.md.
   */
  if (install.stepDue) {
    return (
      <Shell>
        <div className="scroll flush">
          <InstallApp
            guide={install.guide}
            persistent={install.persistent}
            onInstall={install.install}
            onLater={install.later}
            onFinish={install.finish}
          />
        </div>
      </Shell>
    );
  }

  /**
   * The reminder ask, rendered wherever a trigger can fire.
   *
   * Both the larder and the item detail get it, because adding something lands
   * you on the larder and opening something lands you on the detail. It is
   * deliberately NOT rendered over the add flow or the install step: a sheet on
   * top of a form you are mid-way through is an interruption, and the whole
   * argument for asking in context is that the moment has already finished
   * making the case.
   */
  const primer = push.pending ? (
    <NotifyPrimer
      context={push.pending}
      onAllow={push.allow}
      onDismiss={push.dismiss}
      busy={busy}
    />
  ) : null;

  // ── Full-screen flows ────────────────────────────────────────────────
  if (view === "new-place") {
    return (
      <Shell>
        <div className="scroll flush">
          <CreateLocation
            onCreate={handleCreatePlace}
            onCancel={() => setView("settings")}
            busy={busy}
            error={error}
            existingCount={locations.locations.length}
            existingNames={locations.locations.map((l) => l.name)}
          />
        </div>
      </Shell>
    );
  }
  const openCartEntry = cart.entries.find((e) => e.id === openCartId) ?? null;
  if (view === "cart-item" && openCartEntry) {
    return (
      <Shell>
        <div className="scroll flush">
          <CartDetail
            entry={openCartEntry}
            /**
             * Every item ever, not the active ones: the price we are looking
             * for is on the carton that has already been drunk.
             */
            items={larder.items}
            timezone={location?.timezone ?? "Asia/Kolkata"}
            householdId={location.id}
            /**
             * The household's city, for shop prices. Held in state as well as
             * on the document so the screen updates the instant it resolves,
             * rather than waiting on a snapshot — and so it still works for a
             * member whose write the rules refuse (only the owner may).
             */
            where={shopWhere ?? location?.shopLocation ?? null}
            busy={busy}
            onClose={home}
            onFoundWhere={(found) => {
              setShopWhere(found);
              if (location) {
                // Best effort. The session already has the answer; persisting
                // it only saves the NEXT person from being asked.
                void setShopLocation(location.id, found).catch(() => {});
              }
            }}
            onGot={() => handleBuy(openCartEntry.id)}
            onDrop={() =>
              run(async () => {
                if (!location) throw new Error("no household");
                await removeFromCart(location.id, openCartEntry.id);
                cue("remove");
              }, home)
            }
          />
        </div>
        {primer}
      </Shell>
    );
  }

  const openItem = larder.items.find((i) => i.id === openItemId) ?? null;
  if (view === "item" && openItem) {
    return (
      <Shell>
        <div className="scroll flush">
          <ItemDetail
            /**
             * NOT keyed on the item, deliberately.
             *
             * It was, so that opening a sibling packet from the batch timeline
             * remounted the screen and every field re-seeded from the new item.
             * That was correct and far too blunt: two packets of the same thing
             * differ in a date and a shelf, and the remount replayed the whole
             * staggered entrance to say so — nine cards animating to announce
             * that two rows had changed.
             *
             * The screen now resets its own fields when the item id changes and
             * flashes only the rows that actually differ. See ItemDetail.
             */
            item={openItem}
            all={larder.items}
            places={storages.places}
            today={larder.today}
            timezone={location?.timezone ?? "Asia/Kolkata"}
            busy={busy}
            error={error}
            onOpen={goToItem}
            onClose={home}
            /**
             * Saves in place — deliberately NOT followed by `home`.
             *
             * It used to close the screen on success, which was right when a
             * Save button meant "I am finished here". The item screen now
             * autosaves on every change, so keeping that would eject the user
             * back to the list about half a second after touching any control.
             */
            onSave={(patch) =>
              run(async () => {
                if (!location) throw new Error("no household");
                await editItem(location.id, openItem.id, patch);
              })
            }
            onAnswer={(type) => {
              handleAnswer(openItem.id, type);
              home();
            }}
            onBuyAgain={() => {
              handleAddToCart({
                name: openItem.name,
                brand: openItem.brand,
                sizeText: openItem.sizeText,
                reason: "buy_again",
                fromItemId: openItem.id,
              });
              home();
            }}
            cart={cart.entries}
          />
        </div>
        {primer}
      </Shell>
    );
  }
  if (view === "add") {
    return (
      <Shell>
        <div className="scroll flush">
          <AddItem
            today={larder.today}
            places={storages.places}
            onAddPlace={handleAddPlace}
            initial={draft}
            onAdd={handleAdd}
            onCancel={home}
            busy={busy}
            error={error}
            canScan={canScan}
            timezone={location?.timezone ?? "Asia/Kolkata"}
          />
        </div>
      </Shell>
    );
  }

  // ── Tabs ─────────────────────────────────────────────────────────────
  const tab = view as Tab;

  return (
    <Shell>
      <TopBar
        location={location}
        all={locations.locations}
        onSelect={locations.select}
        onAddPlace={() => setView("new-place")}
        onOpenSettings={() => setView("settings")}
        headline={headline(larder)}
        sub={subline(larder)}
      />

      {tab === "larder" && (
        <>
          <Larder
            state={larder}
            places={storages.places}
            busy={busy}
            cart={cart.entries}
            onAnswer={handleAnswer}
            onBuy={handleBuy}
            onOpenCart={openCart}
            onAddToCart={(name) => handleAddToCart({ name, reason: "search_miss" })}
            onOpen={goToItem}
            banner={
              install.nudgeDue ? (
                <InstallNudge onOpen={install.reopen} oneTap={install.guide.automatic} />
              ) : null
            }
          />
          {ranOut && (
            <RebuyPrompt
              name={ranOut.name}
              busy={busy}
              onVerdict={handleVerdict}
              onClose={() => setRanOut(null)}
              onAddToCart={() => {
                handleAddToCart({
                  name: ranOut.name,
                  reason: "ran_out" satisfies CartReason,
                  fromItemId: ranOut.id,
                });
                setRanOut(null);
              }}
            />
          )}
          <AddFab
            onAdd={() => {
              setDraft(null);
              setView("add");
            }}
          />
        </>
      )}

      {tab === "calendar" && (
        <div className="scroll flush screen" key="calendar">
          <Calendar state={larder} />
        </div>
      )}

      {tab === "settings" && (
        <div className="scroll flush screen" key="settings">
          <Settings
            session={session}
            onSignOut={auth.signOut}
            households={locations.locations}
            currentHousehold={location.id}
            onRenameHousehold={(id, name) =>
              run(() =>
                renameLocation(
                  id,
                  name,
                  locations.locations.filter((l) => l.id !== id).map((l) => l.name),
                ),
              )
            }
            countHouseholdItems={countActiveItems}
            onRemoveHousehold={(id) =>
              run(async () => {
                if (!uid) throw new Error("not signed in");
                // Step off the place before it goes, so the screens under this
                // one never render against an id that no longer exists.
                const other = locations.locations.find((l) => l.id !== id);
                if (!other) throw new Error("keep at least one place");
                if (location.id === id) locations.select(other.id);
                await deleteLocation(uid, id);
              })
            }
            busy={auth.busy}
            error={auth.error}
            installed={install.installed}
            onInstall={install.reopen}
            push={push}
            cues={cues}
            places={storages.places}
            onAddStorage={(name, kind) =>
              run(async () => {
                if (!location) return;
                await createStorage(location.id, { name, kind }, storages.places.length);
              })
            }
            onRemoveStorage={(id) =>
              run(async () => {
                if (!location) return;
                await deleteStorage(location.id, id);
              })
            }
          />
          <Diagnostics session={session} />
        </div>
      )}

      {primer}

      <BottomNav
        active={tab}
        onGo={setView}
        badges={{
          larder: larder.needsAnswer.length,
        }}
      />
    </Shell>
  );
}

/**
 * `wide` is for the landing page only. The app proper stays a phone-width
 * column on every screen — a to-do list stretched across a 27" monitor is
 * harder to read, not easier — but the landing page is a document, and a
 * document confined to 30rem on a desktop reads as a mobile site someone
 * forgot to finish.
 */
function Shell({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return <main className={wide ? "wide" : undefined}>{children}</main>;
}

/**
 * The number in the header. Larder's equivalent of "delivery in 10 minutes":
 * the one fact that decides whether the app was worth opening.
 */
function headline(s: ReturnType<typeof useLarder>): string {
  if (s.status !== "ready") return "Larder";
  const n = s.needsAnswer.length;
  if (n > 0) return `${n} thing${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} you`;
  if (s.items.length === 0) return "Let's start your larder";
  return "Nothing needs you today";
}

function subline(s: ReturnType<typeof useLarder>): string {
  if (s.status !== "ready") return "";
  if (s.needsAnswer.length > 0) return "Two taps and you're done.";
  if (s.items.length === 0) return "Add the thing most likely to go off.";
  if (s.streak > 0) return `${s.streak}-day clean streak · ${s.items.length} tracked`;
  return `${s.items.length} tracked · nothing due`;
}

function Blocked({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="scroll flush">
      <section className="hint" data-testid="blocked">
        <strong>{title}</strong>
        <p>{detail}</p>
        <p className="dim">
          Run <code>npm run emulators</code> in another terminal, then reload.
        </p>
      </section>
    </div>
  );
}
